import { useTranslation } from "react-i18next";
import { MessageCircle } from "lucide-react";

import {
    paymentNoticesOnWhatsApp,
    sessionNoticesOnWhatsApp,
    type ReminderChannels,
} from "../../domain/ReminderChannels";

/**
 * What the notifications left switched on actually send.
 *
 * Shown only where the deployment can put at least one of this tool's
 * notifications on WhatsApp, because the therapist has nothing to learn
 * otherwise: email is what a notification has always been, and a note saying
 * so is noise.
 *
 * It takes the channels from the settings screen rather than asking again, so
 * the rows' channel controls and this notice read the same answer and can
 * never disagree after one of two requests fails.
 *
 * It says as plainly as it can that WhatsApp is not the therapist's to switch
 * on. Meta only lets a business open a conversation with someone who asked
 * for it, and the number is the patient's own — so the consent is given in
 * the patient's portal, by the patient, and can be withdrawn there. A
 * therapist who believed they had enabled WhatsApp for everyone would stop
 * watching the one channel that does reach everyone.
 *
 * It complements the short note under each row's WhatsApp options, and does
 * not replace it: that one is read at the moment of choosing.
 */
export default function ReminderChannelsNotice({
    kind,
    channels,
}: {
    kind: "session" | "payment";
    channels: ReminderChannels;
}) {
    const { t } = useTranslation();

    const onWhatsApp =
        kind === "session"
            ? sessionNoticesOnWhatsApp(channels)
            : paymentNoticesOnWhatsApp(channels);

    if (!onWhatsApp) return null;

    return (
        <div
            role="note"
            className="flex items-start gap-3 rounded-xl border border-status-info bg-status-info-tint p-4 text-sm text-status-info-on-tint"
        >
            <MessageCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex flex-col gap-1">
                <span className="font-medium">
                    {t("notifications.reminderChannels.whatsappTitle")}
                </span>
                <span>
                    {t(
                        kind === "session"
                            ? "notifications.reminderChannels.whatsappSessionBody"
                            : "notifications.reminderChannels.whatsappPaymentBody"
                    )}
                </span>
                <span>
                    {t("notifications.reminderChannels.whatsappConsent")}
                </span>
            </div>
        </div>
    );
}
